import { Container, Graphics, Sprite, Text } from 'pixi.js'
import gsap from 'gsap'
import { formatMoney } from '../../framework/core/format'
import { layoutMetrics, type Orientation, type Resizable } from '../../framework/render/design'
import { SpriteButton } from '../../framework/ui/SpriteButton'
import { bottomBarTexture } from '../assets/BottomBarTextures'
import type { GameBus } from '../events/GameEvents'
import type { PlayerState } from '../state/PlayerState'

// 下方操作列 — 對齊真實遊戲：深色橫條 bar。
// 左：menu/info/wallet 圖示 + 餘額；中：跑馬燈；右：押注 + EXTRA/TURBO/AUTO + SPIN。
const AUTO_DEFAULT_COUNT = 10
const MARQUEE_TEXT = '　倍數轉輪 與 招財虎 可觸發 BONUS　★　ExtraBet 加注提高中獎機會　★'
const MARQUEE_SPEED = 90 // px/秒

export class BottomBarView extends Container implements Resizable {
  private readonly barBg = new Graphics()
  private readonly menuIcon = new Sprite(bottomBarTexture('btn_set'))
  private readonly infoIcon = new Sprite(bottomBarTexture('btn_info'))
  private readonly walletIcon = new Sprite(bottomBarTexture('btn_wallet'))
  private readonly balanceCaption = BottomBarView.caption('餘額')
  private readonly balanceValue = BottomBarView.value()
  private readonly marqueeBg = new Sprite(bottomBarTexture('pic_marquee_1'))
  private readonly marqueeText: Text
  private readonly marqueeMask = new Graphics()
  private marqueeTween: gsap.core.Tween | null = null
  private readonly betCaption = BottomBarView.caption('押注')
  private readonly betValue = BottomBarView.value()
  private readonly spinButton: SpriteButton
  private readonly betMinus: SpriteButton
  private readonly betPlus: SpriteButton
  private readonly autoButton: SpriteButton
  private readonly turboButton: SpriteButton

  constructor(bus: GameBus, player: PlayerState) {
    super()

    this.spinButton = new SpriteButton(
      bottomBarTexture('btn_spin_up'),
      bottomBarTexture('btn_spin_stop'),
      () => bus.emit('spinRequested', { buyType: player.extraBet ? 1 : 0 }),
    )
    this.betMinus = new SpriteButton(bottomBarTexture('btn_minus'), null, () => {
      player.cycleBet(-1)
      bus.emit('betChanged', { bet: player.bet })
    })
    this.betPlus = new SpriteButton(bottomBarTexture('btn_plus'), null, () => {
      player.cycleBet(1)
      bus.emit('betChanged', { bet: player.bet })
    })
    this.turboButton = new SpriteButton(
      bottomBarTexture('btn_fast_normal'),
      bottomBarTexture('btn_fast_quick'),
      () => {
        player.turbo = !player.turbo
        this.turboButton.setActive(player.turbo)
        bus.emit('turboChanged', { turbo: player.turbo })
      },
    )
    this.autoButton = new SpriteButton(
      bottomBarTexture('btn_auto'),
      bottomBarTexture('btn_auto_on'),
      () => {
        if (player.autoActive) {
          player.cancelAuto()
          bus.emit('autoSpinChanged', { active: false, remaining: 0 })
        } else {
          player.startAuto(AUTO_DEFAULT_COUNT)
          bus.emit('autoSpinChanged', { active: true, remaining: AUTO_DEFAULT_COUNT })
          bus.emit('spinRequested', { buyType: player.extraBet ? 1 : 0 })
        }
        this.autoButton.setActive(player.autoActive)
      },
    )

    for (const icon of [this.menuIcon, this.infoIcon, this.walletIcon]) {
      icon.anchor.set(0.5)
    }
    this.marqueeBg.anchor.set(0.5)
    this.marqueeText = new Text({
      text: MARQUEE_TEXT,
      style: { fill: 0xffe9a8, fontWeight: 'bold', fontFamily: 'Arial', fontSize: 20 },
    })
    this.marqueeText.anchor.set(0, 0.5)
    this.marqueeText.mask = this.marqueeMask

    this.balanceValue.text = formatMoney(player.balance)
    this.betValue.text = formatMoney(player.bet)
    this.betCaption.anchor.set(0.5, 0)
    this.betValue.anchor.set(0.5, 0.5)

    this.addChild(
      this.barBg,
      this.menuIcon,
      this.infoIcon,
      this.walletIcon,
      this.balanceCaption,
      this.balanceValue,
      this.marqueeBg,
      this.marqueeText,
      this.marqueeMask,
      this.betCaption,
      this.betMinus,
      this.betValue,
      this.betPlus,
      this.turboButton,
      this.autoButton,
      this.spinButton,
    )

    bus.on('balanceChanged', ({ balance }) => {
      this.balanceValue.text = formatMoney(balance)
    })
    bus.on('betChanged', () => {
      this.betValue.text = formatMoney(player.bet)
    })
    bus.on('autoSpinChanged', ({ active }) => this.autoButton.setActive(active))
    bus.on('uiBusy', ({ busy }) => this.setBusy(busy))
  }

  private setBusy(busy: boolean): void {
    this.spinButton.setActive(busy)
    this.spinButton.setEnabled(!busy)
    this.betMinus.setEnabled(!busy)
    this.betPlus.setEnabled(!busy)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const barHeight = height * layoutMetrics(orientation).barHeight
    const top = height - barHeight

    this.barBg.clear()
    this.barBg.rect(0, top, width, barHeight).fill({ color: 0x140c1e, alpha: 0.82 })
    this.barBg.rect(0, top, width, Math.max(1, barHeight * 0.03)).fill({ color: 0xffd24a, alpha: 0.25 })

    if (orientation === 'portrait') {
      this.layoutPortrait(width, top, barHeight)
    } else {
      this.layoutLandscape(width, top, barHeight)
    }
  }

  // 橫版單列：menu/info/wallet + 餘額(左) ｜ 跑馬燈(中) ｜ 押注 + EXTRA/TURBO/AUTO + SPIN(右)
  private layoutLandscape(width: number, top: number, barHeight: number): void {
    this.betCaption.visible = true // 直版會隱藏，切回橫版需還原
    const centerY = top + barHeight / 2
    const margin = width * 0.025
    const gap = barHeight * 0.16
    const captionSize = Math.max(11, barHeight * 0.2)
    const valueSize = Math.max(16, barHeight * 0.32)

    // 左：圖示叢集
    const iconH = barHeight * 0.5
    let lx = margin
    const mw = this.fitIcon(this.menuIcon, iconH)
    this.menuIcon.position.set(lx + mw / 2, centerY)
    lx += mw + gap
    const iw = this.fitIcon(this.infoIcon, iconH)
    this.infoIcon.position.set(lx + iw / 2, centerY)
    lx += iw + gap
    const ww = this.fitIcon(this.walletIcon, iconH)
    this.walletIcon.position.set(lx + ww / 2, centerY)
    lx += ww + gap * 0.6

    // 餘額（圖示右側）
    this.balanceCaption.style.fontSize = captionSize
    this.balanceValue.style.fontSize = valueSize
    this.balanceCaption.position.set(lx, top + barHeight * 0.18)
    this.balanceValue.position.set(lx, top + barHeight * 0.46)

    // 右側叢集（由右往左）：SPIN → AUTO → TURBO → 押注步進器（ExtraBet 開關移至左下角）
    this.spinButton.setHeight(barHeight * 1.25)
    const sideH = barHeight * 0.6
    this.turboButton.setHeight(sideH)
    this.autoButton.setHeight(sideH)
    this.betMinus.setHeight(barHeight * 0.5)
    this.betPlus.setHeight(barHeight * 0.5)
    this.betValue.style.fontSize = valueSize
    this.betCaption.style.fontSize = captionSize

    let x = width - margin - this.spinButton.displayWidth / 2
    this.spinButton.position.set(x, centerY - barHeight * 0.05)
    x -= this.spinButton.displayWidth / 2 + gap + this.autoButton.displayWidth / 2
    this.autoButton.position.set(x, centerY)
    x -= this.autoButton.displayWidth / 2 + gap + this.turboButton.displayWidth / 2
    this.turboButton.position.set(x, centerY)
    // 押注步進器
    x -= this.turboButton.displayWidth / 2 + gap * 1.4 + this.betPlus.displayWidth / 2
    this.betPlus.position.set(x, centerY + barHeight * 0.06)
    x -= this.betPlus.displayWidth / 2 + gap * 0.5 + this.betValue.width / 2
    this.betValue.position.set(x, centerY + barHeight * 0.06)
    this.betCaption.position.set(x, top + barHeight * 0.1)
    x -= this.betValue.width / 2 + gap * 0.5 + this.betMinus.displayWidth / 2
    this.betMinus.position.set(x, centerY + barHeight * 0.06)

    // 中央跑馬燈（左叢集右緣 ~ 押注左緣 之間）
    const leftEdge = lx + this.balanceValue.width + gap
    const rightEdge = x - this.betMinus.displayWidth / 2 - gap
    this.layoutMarquee((leftEdge + rightEdge) / 2, centerY, Math.max(barHeight * 2, rightEdge - leftEdge), barHeight)
  }

  // 直版（對齊截圖2）：大圓 SPIN 置中；左＝餘額＋圖示，右＝EXTRA/TURBO/AUTO，
  // SPIN 下方＝押注步進器；跑馬燈置於 bar 上方（框與 bar 之間的空隙）。
  private layoutPortrait(width: number, top: number, barHeight: number): void {
    const cx = width / 2
    const margin = width * 0.04
    const gap = barHeight * 0.12
    const captionSize = Math.max(11, barHeight * 0.1)
    const valueSize = Math.max(14, barHeight * 0.16)

    // 中央大圓 SPIN
    const spinSize = Math.min(barHeight * 0.54, width * 0.24)
    this.spinButton.setHeight(spinSize)
    const spinCy = top + barHeight * 0.36
    this.spinButton.position.set(cx, spinCy)

    // SPIN 下方：押注步進器 [−] 金額 [+]（直版隱藏「押注」字樣，金額即足夠）
    this.betCaption.visible = false
    this.betMinus.setHeight(barHeight * 0.24)
    this.betPlus.setHeight(barHeight * 0.24)
    this.betValue.style.fontSize = valueSize
    const betCy = top + barHeight * 0.8
    this.betValue.position.set(cx, betCy)
    const betOffset = this.betValue.width / 2 + gap + this.betMinus.displayWidth / 2
    this.betMinus.position.set(cx - betOffset, betCy)
    this.betPlus.position.set(cx + betOffset, betCy)

    // 左叢集：餘額（上）＋ menu/info/wallet 圖示（下）
    this.balanceCaption.style.fontSize = captionSize
    this.balanceValue.style.fontSize = valueSize
    this.balanceCaption.position.set(margin, top + barHeight * 0.14)
    this.balanceValue.position.set(margin, top + barHeight * 0.32)

    const iconH = barHeight * 0.26
    const iconY = top + barHeight * 0.68
    let lx = margin
    const mw = this.fitIcon(this.menuIcon, iconH)
    this.menuIcon.position.set(lx + mw / 2, iconY)
    lx += mw + gap
    const iw = this.fitIcon(this.infoIcon, iconH)
    this.infoIcon.position.set(lx + iw / 2, iconY)
    lx += iw + gap
    const ww = this.fitIcon(this.walletIcon, iconH)
    this.walletIcon.position.set(lx + ww / 2, iconY)

    // 右叢集：上排 TURBO、下排 AUTO（皆靠右緣，避開中央 SPIN；ExtraBet 開關移至左下角）
    const sideH = barHeight * 0.3
    this.turboButton.setHeight(sideH)
    this.autoButton.setHeight(sideH)
    const topRowY = top + barHeight * 0.3
    this.turboButton.position.set(width - margin - this.turboButton.displayWidth / 2, topRowY)
    this.autoButton.position.set(width - margin - this.autoButton.displayWidth / 2, top + barHeight * 0.68)

    // 跑馬燈：bar 上方空隙（框與 bar 之間），橫跨中央
    this.layoutMarquee(cx, top - barHeight * 0.2, width * 0.9, barHeight * 0.66)
  }

  // 跑馬燈：底圖 + 遮罩裁切 + 文字水平捲動
  private layoutMarquee(centerX: number, centerY: number, windowW: number, barHeight: number): void {
    const h = barHeight * 0.5
    if (windowW < barHeight) {
      this.marqueeBg.visible = false
      this.marqueeText.visible = false
      return
    }
    this.marqueeBg.visible = true
    this.marqueeText.visible = true

    const bgTex = this.marqueeBg.texture
    if (bgTex.width > 0 && bgTex.height > 0) {
      this.marqueeBg.scale.set(windowW / bgTex.width, h / bgTex.height)
    }
    this.marqueeBg.position.set(centerX, centerY)

    const padX = h * 0.3
    this.marqueeMask.clear()
    this.marqueeMask.rect(centerX - windowW / 2 + padX, centerY - h / 2, windowW - padX * 2, h).fill({ color: 0xffffff })

    this.marqueeText.style.fontSize = Math.max(12, h * 0.42)
    this.marqueeText.y = centerY
    const startX = centerX + windowW / 2 - padX
    const endX = centerX - windowW / 2 + padX - this.marqueeText.width
    this.marqueeText.x = startX

    this.marqueeTween?.kill()
    this.marqueeTween = gsap.to(this.marqueeText, {
      x: endX,
      duration: (startX - endX) / MARQUEE_SPEED,
      ease: 'none',
      repeat: -1,
    })
  }

  private fitIcon(icon: Sprite, height: number): number {
    const tex = icon.texture
    if (tex.height <= 0) return height
    icon.scale.set(height / tex.height)
    return (tex.width * height) / tex.height
  }

  private static caption(text: string): Text {
    const t = new Text({ text, style: { fill: 0x9aa0d0, fontFamily: 'Arial', fontSize: 14 } })
    t.anchor.set(0, 0)
    return t
  }

  private static value(): Text {
    const t = new Text({
      text: '0',
      style: { fill: 0xffffff, fontWeight: 'bold', fontFamily: 'Arial', fontSize: 22 },
    })
    t.anchor.set(0, 0)
    return t
  }
}
